import React, { useEffect, useState } from "react";
import { Users, Loader2, Search } from "lucide-react";
import toast from "react-hot-toast";
import axios from "../../api/axios";

import {
  masterPanelCard,
  masterHeadingMain,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
} from "../../GlobalComponents/MasterCssClass";

const FacultyView = () => {
  const [faculties, setFaculties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("ALL");

  const fetchFaculties = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/public/faculty-view/all");
      setFaculties(res.data?.data || []);
    } catch {
      toast.error("FAILED TO LOAD FACULTY DETAILS");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaculties();
  }, []);

  const uniqueSubjects = ["ALL", ...Array.from(new Set(faculties.map((f) => String(f.subject || "").trim().toUpperCase())))];

  // Helper weight function to prioritize senior designations at the top
  const getDesignationWeight = (designation) => {
    const des = String(designation || "").toUpperCase();
    if (des.includes("SENIOR") || des.includes("HOD") || des.includes("PROFESSOR")) return 1;
    if (des.includes("FACULTY") || des.includes("ASSISTANT")) return 2;
    return 3;
  };

  const filteredFaculties = faculties
    .filter((fac) => {
      const matchesSearch =
        !searchTerm.trim() ||
        fac.teacherName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fac.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fac.designation?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesSubject =
        selectedSubject === "ALL" || String(fac.subject || "").trim().toUpperCase() === selectedSubject;

      return matchesSearch && matchesSubject;
    })
    .sort((a, b) => {
      // 1. Sort by designation weight (Senior / HOD / Professor first)
      const weightA = getDesignationWeight(a.designation);
      const weightB = getDesignationWeight(b.designation);
      if (weightA !== weightB) {
        return weightA - weightB;
      }

      // 2. Sort by creation date (Old faculty first, new faculty last)
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateA - dateB;
    });

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-hidden p-3`}>
      {/* FIXED TOP SECTION (Header + Filters do not scroll) */}
      <div className="shrink-0 flex flex-col gap-1 pb-1 bg-slate-50 z-10">
        <div className={`${masterPanelCard} p-1 mb-0`}>
          <div className={masterHeaderContent}>
            <div className={masterHeaderBrand}>
              <div className={masterHeaderIconBox}>
                <Users size={16} />
              </div>
              <div>
                <h1 className={masterHeadingMain}>Faculty</h1>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center gap-3">
          <div className="relative flex-1 w-full">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search faculty by name or subject..."
              className="w-full pl-9 pr-4 py-2 bg-white rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 outline-none focus:border-blue-500 shadow-xs"
            />
          </div>

          <div className="w-full md:w-56">
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="w-full px-3 py-2 bg-white rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 outline-none focus:border-blue-500 shadow-xs uppercase"
            >
              {uniqueSubjects.map((sub) => (
                <option key={sub} value={sub}>
                  {sub === "ALL" ? "All Subjects" : sub}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* SCROLLABLE GRID CONTAINER */}
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 pt-1">
        {loading ? (
          <div className="flex items-center justify-center h-full py-20 text-slate-500 font-semibold gap-2">
            <Loader2 size={20} className="animate-spin text-[#0E3652]" /> Loading Faculty Directory...
          </div>
        ) : filteredFaculties.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-1">
            {filteredFaculties.map((fac) => (
              <div key={fac._id} className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 flex flex-col gap-4 hover:shadow-md transition-all">
                <div className="flex items-start gap-4">
                  {/* Larger Image Display Style */}
                  <div className="h-20 w-20 rounded-full border-2 border-slate-200 overflow-hidden bg-slate-100 flex items-center justify-center shrink-0 shadow-xs">
                    {fac.photoPath ? (
                      <img src={fac.photoPath} alt={fac.teacherName} className="h-full w-full object-cover" />
                    ) : (
                      <Users size={32} className="text-slate-400" />
                    )}
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <h3 className="font-black text-sm text-[#0E3652] uppercase truncate tracking-tight">{fac.teacherName}</h3>
                    <span className="text-xs font-bold text-slate-600 uppercase mt-0.5">{fac.designation}</span>
                    {fac.subject && (
                      <div className="mt-2">
                        <span className="inline-block px-3 py-0.5 rounded-full text-[11px] font-black bg-blue-50 text-blue-700 border border-blue-100 uppercase tracking-wide">
                          {fac.subject}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {fac.description && (
                  <p className="text-xs text-slate-600 leading-relaxed font-normal pt-3 border-t border-slate-100">
                    {fac.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full py-20 text-slate-400 font-bold uppercase tracking-wider text-xs">
            No Active Faculty Members Found
          </div>
        )}
      </div>
    </div>
  );
};

export default FacultyView;